import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Share2, ExternalLink } from "lucide-react";

/** Invite-a-friend card for the Dashboard. Fetches the user's referral code
 *  and stats, and lets them share the link via native share or clipboard. */
export default function ReferralCard() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/referral/me").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  if (!info) return null;

  const link = `${window.location.origin}/register?ref=${info.code}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied!"); }
    catch { toast.error("Copy failed — long-press to select manually"); }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on StenoTypingBooster",
          text: "Practice court reporting speed with me — 7 free bonus days for both of us when you complete your first attempt.",
          url: link,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };
  const whatsapp = () => {
    const text = encodeURIComponent(`Practice steno/typing with me on StenoTypingBooster — 7 free bonus days for both of us: ${link}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  return (
    <div className="border border-brand-gold/40 bg-brand-gold/10 rounded-2xl p-6 mb-10" data-testid="referral-card">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-brand-gold text-background flex items-center justify-center shrink-0">
          <Gift className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-1">Invite a friend</div>
          <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight">Dono ko 7 din bonus milega</h3>
          <p className="mt-1 font-mono text-sm text-muted-foreground">
            Dost signup kare aur pehli practice complete kare → aap dono ko <b className="text-foreground">7 din ka extra subscription</b> mil jayega.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center min-w-[180px]">
          <div className="border border-border rounded-lg bg-background/60 px-3 py-2">
            <div className="text-2xl font-black tracking-tight" data-testid="referral-invited">{info.invited_count}</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Invited</div>
          </div>
          <div className="rounded-lg bg-brand-gold text-background px-3 py-2">
            <div className="text-2xl font-black tracking-tight" data-testid="referral-earned">{info.bonus_earned_count * 7}</div>
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold">Bonus days</div>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px] border border-border rounded-lg bg-background/70 px-3 py-2 font-mono text-xs md:text-sm truncate" data-testid="referral-link">
          {link}
        </div>
        <button onClick={copy} data-testid="referral-copy-btn" className="h-10 px-4 border border-border rounded-lg hover:bg-secondary text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
        <button onClick={whatsapp} data-testid="referral-whatsapp-btn" className="h-10 px-4 rounded-lg bg-[#25D366] hover:bg-[#1FB558] text-white text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2">
          <ExternalLink className="w-3.5 h-3.5" /> WhatsApp
        </button>
        <button onClick={share} data-testid="referral-share-btn" className="h-10 px-4 rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-background text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
      </div>
    </div>
  );
}
